import React, { useState } from 'react';
import { WebEnrichmentData } from '../../web_enrichment/types';
import { WebEnrichmentEditMode } from './WebEnrichmentEditMode';

interface Props {
  webEnrichmentData: WebEnrichmentData | null;
  isLoading: boolean;
  accentColor?: string;
  canEdit?: boolean;
  companyId?: number;
  onDataUpdated?: (updated: WebEnrichmentData) => void;
}

export const WebEnrichmentDisplay: React.FC<Props> = ({
  webEnrichmentData,
  isLoading,
  accentColor = '#3B82F6',
  canEdit = false,
  companyId,
  onDataUpdated,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localData, setLocalData] = useState<WebEnrichmentData | null>(webEnrichmentData);

  // Keep localData in sync when the parent refreshes the prop (e.g., page reload)
  React.useEffect(() => {
    if (!isEditing) {
      setLocalData(webEnrichmentData);
    }
  }, [webEnrichmentData, isEditing]);

  const displayData = localData ?? webEnrichmentData;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-transparent" style={{ borderColor: accentColor, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!displayData?.extracted_data || Object.keys(displayData.extracted_data).length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
        <p className="text-sm">No website enrichment data available</p>
        <p className="text-xs mt-1">Run web enrichment to extract data from the company's website.</p>
      </div>
    );
  }

  const handleSaved = (updated: WebEnrichmentData) => {
    setLocalData(updated);
    setIsEditing(false);
    onDataUpdated?.(updated);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const d = displayData.extracted_data;
  const badgeGroups = [
    { key: 'awards', label: 'Awards', bg: '#fefce8', color: '#a16207' },
    { key: 'partnerships', label: 'Partnerships', bg: '#eff6ff', color: '#1d4ed8' },
    { key: 'clients', label: 'Clients', bg: '#f0fdfa', color: '#0f766e' },
    { key: 'certifications', label: 'Certifications', bg: '#f0fdf4', color: '#15803d' },
  ];

  return (
    <div className="space-y-6">
      {/* Edit toggle header */}
      {canEdit && companyId != null && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {displayData.has_user_edits && !isEditing && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                Edited
              </span>
            )}
          </div>
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80"
              style={{ backgroundColor: accentColor + '15', color: accentColor, border: `1px solid ${accentColor}30` }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          )}
        </div>
      )}

      {/* Edit mode */}
      {isEditing && canEdit && companyId != null && displayData && (
        <WebEnrichmentEditMode
          webEnrichmentData={displayData}
          companyId={companyId}
          accentColor={accentColor}
          onSaved={handleSaved}
          onCancel={handleCancel}
        />
      )}

      {/* Read mode */}
      {!isEditing && (
        <>
          {/* Identity */}
          {(d.tagline || d.mission || d.vision) && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Identity</h4>
              <div className="space-y-3">
                {d.tagline && <div className="p-3 rounded-lg bg-blue-50 border border-blue-100"><p className="text-xs font-medium text-blue-600 mb-1">Tagline</p><p className="text-sm text-gray-900 italic">"{d.tagline}"</p></div>}
                {d.mission && <div className="p-3 rounded-lg bg-gray-50 border border-gray-200"><p className="text-xs font-medium text-gray-500 mb-1">Mission</p><p className="text-sm text-gray-800">{d.mission}</p></div>}
                {d.vision && <div className="p-3 rounded-lg bg-gray-50 border border-gray-200"><p className="text-xs font-medium text-gray-500 mb-1">Vision</p><p className="text-sm text-gray-800">{d.vision}</p></div>}
              </div>
            </div>
          )}

          {/* Products & Services */}
          {(d.products?.length || d.services?.length || d.product_categories?.length) ? (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Products & Services</h4>
              {d.products?.length > 0 && <div className="mb-3"><p className="text-xs font-medium text-gray-500 mb-2">Products</p><div className="flex flex-wrap gap-2">{d.products.map((p: string, i: number) => <span key={i} className="px-3 py-1 bg-purple-50 text-purple-700 text-xs rounded-full border border-purple-100">{p}</span>)}</div></div>}
              {d.services?.length > 0 && <div className="mb-3"><p className="text-xs font-medium text-gray-500 mb-2">Services</p><div className="flex flex-wrap gap-2">{d.services.map((s: string, i: number) => <span key={i} className="px-3 py-1 bg-green-50 text-green-700 text-xs rounded-full border border-green-100">{s}</span>)}</div></div>}
              {d.product_categories?.length > 0 && <div><p className="text-xs font-medium text-gray-500 mb-2">Categories</p><div className="flex flex-wrap gap-2">{d.product_categories.map((c: string, i: number) => <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">{c}</span>)}</div></div>}
            </div>
          ) : null}

          {/* Technology & Innovation */}
          {(d.technologies?.length || d.innovations?.length) ? (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Technology & Innovation</h4>
              {d.technologies?.length > 0 && <div className="mb-3"><p className="text-xs font-medium text-gray-500 mb-2">Technologies</p><div className="flex flex-wrap gap-2">{d.technologies.map((t: string, i: number) => <span key={i} className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full border border-indigo-100">{t}</span>)}</div></div>}
              {d.innovations?.length > 0 && <div><p className="text-xs font-medium text-gray-500 mb-2">Innovations</p><div className="flex flex-wrap gap-2">{d.innovations.map((inv: string, i: number) => <span key={i} className="px-3 py-1 bg-amber-50 text-amber-700 text-xs rounded-full border border-amber-100">{inv}</span>)}</div></div>}
            </div>
          ) : null}

          {/* Leadership Team */}
          {d.leadership_team?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Leadership Team</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {d.leadership_team.map((leader: any, i: number) => (
                  <div key={i} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                    <p className="text-sm font-medium text-gray-900">{typeof leader === 'string' ? leader : leader?.name || 'Unknown'}</p>
                    {typeof leader === 'object' && leader?.title && <p className="text-xs text-gray-500 mt-0.5">{leader.title}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Awards / Partnerships / Clients / Certifications */}
          {badgeGroups.map(({ key, label, bg, color }) => {
            const items = d[key];
            if (!Array.isArray(items) || items.length === 0) return null;
            return (
              <div key={key}>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{label}</h4>
                <div className="flex flex-wrap gap-2">
                  {items.map((item: string, i: number) => (
                    <span key={i} className="px-3 py-1 text-xs rounded-full border" style={{ backgroundColor: bg, color, borderColor: color + '40' }}>
                      {typeof item === 'string' ? item : JSON.stringify(item)}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Contact & Social */}
          {(d.contact_email || d.phone || d.social_media) && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Contact & Social</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {d.contact_email && (
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">Email</p>
                    <a href={`mailto:${d.contact_email}`} className="text-sm text-blue-600 hover:underline">{d.contact_email}</a>
                  </div>
                )}
                {d.phone && (
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">Phone</p>
                    <p className="text-sm text-gray-900">{d.phone}</p>
                  </div>
                )}
              </div>
              {d.social_media && typeof d.social_media === 'object' && Object.keys(d.social_media).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-500 mb-2">Social Media</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(d.social_media).map(([platform, url]) => (
                      <a key={platform} href={url as string} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-lg hover:bg-gray-200 transition-colors capitalize">
                        {platform}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-xs text-gray-400">
              {displayData.website_url && <span>Source: {displayData.website_url}</span>}
              <span>
                {displayData.fields_extracted}/{displayData.total_possible_fields} fields
                {displayData.extraction_completed_at && (
                  <> &middot; {new Date(displayData.extraction_completed_at).toLocaleDateString()}</>
                )}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
